/**
 * Paying a kitchen the money ChooseChow holds for it.
 *
 * Two ways, and the dialog keeps them visibly apart because they mean different
 * things. "Send with Paystack" moves money now, and is only offered when the
 * kitchen is verified with an active payout account — the same rule the
 * automatic payout follows. "Record a payment" moves nothing: it records money
 * operations already paid outside Paystack, so it needs the bank reference and
 * a reason, and it is final.
 *
 * Both send the held total the operator is looking at. If it changed since the
 * page loaded, the server refuses and nothing is paid.
 */

import { useState, type FormEvent } from 'react'
import { MinimalAlert, MinimalButton, MinimalInput } from '@ovasabi/ui-minimal'
import { ChowModal } from '../../components/chow'
import { commandPayload, commands } from '../../lib/runtime'
import * as S from './OpsStyle'
import { formatNaira } from './opsData'

export interface PayoutTarget {
  vendorProfileId: string
  kitchenName: string
  heldMinor: number
  payable: boolean
}

type Mode = 'transfer' | 'manual'

const statusCopy: Record<string, string> = {
  paid: 'Paid.',
  processing: 'Sent to Paystack. It will show as paid when Paystack confirms the transfer.',
  failed: 'Paystack did not send it. The money is held again.',
}

export function OpsPayoutDialog({ target, onClose, onDone }: { target: PayoutTarget; onClose: () => void; onDone: () => void }) {
  const [mode, setMode] = useState<Mode>(target.payable ? 'transfer' : 'manual')
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<{ status: string; reason: string } | null>(null)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (busy) return
    if (mode === 'manual' && (!reference.trim() || !note.trim())) {
      setError('Enter the bank reference and a short note about the payment.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const response = commandPayload(await commands.billing.releaseChefPayout({
        vendor_profile_id: target.vendorProfileId,
        expected_amount_minor: String(target.heldMinor),
        method: mode === 'transfer' ? 'paystack_transfer' : 'manual',
        external_reference: reference.trim(),
        note: note.trim(),
      })) as Record<string, unknown>
      setResult({ status: String(response.status ?? ''), reason: String(response.failure_reason ?? '') })
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The payout could not be recorded.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <ChowModal open title={`Pay ${target.kitchenName || 'kitchen'}`} onClose={onClose}>
      <S.Form onSubmit={submit}>
        <S.Note>Held for this kitchen: <strong>{formatNaira(target.heldMinor)}</strong></S.Note>
        {result ? (
          <MinimalAlert tone={result.status === 'failed' ? 'danger' : 'success'} title={result.status === 'failed' ? 'Not paid' : 'Payout recorded'}>
            {statusCopy[result.status] ?? result.status}{result.reason ? ` ${result.reason}` : ''}
          </MinimalAlert>
        ) : (
          <>
            <S.Choice role="radiogroup" aria-label="How to pay">
              <label>
                <input type="radio" name="payout-mode" checked={mode === 'transfer'} disabled={!target.payable || busy} onChange={() => setMode('transfer')} />
                Send with Paystack now
              </label>
              <label>
                <input type="radio" name="payout-mode" checked={mode === 'manual'} disabled={busy} onChange={() => setMode('manual')} />
                Record a payment made outside Paystack
              </label>
            </S.Choice>
            {!target.payable && (
              <S.Note>
                Paystack transfers need a verified kitchen with an active payout account. Until then you can
                only record a payment you made yourself.
              </S.Note>
            )}
            {mode === 'manual' && (
              <S.FormGrid>
                <MinimalInput label="Bank reference" required value={reference} disabled={busy} onChange={(e) => setReference(e.target.value)} />
                <MinimalInput label="Note" required value={note} disabled={busy} placeholder="Who paid, from which account" onChange={(e) => setNote(e.target.value)} />
              </S.FormGrid>
            )}
            {error && <MinimalAlert tone="danger" title="Not paid">{error}</MinimalAlert>}
            <MinimalButton type="submit" loading={busy} disabled={busy}>
              {mode === 'transfer' ? `Send ${formatNaira(target.heldMinor)}` : `Record ${formatNaira(target.heldMinor)} as paid`}
            </MinimalButton>
          </>
        )}
      </S.Form>
    </ChowModal>
  )
}
